import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import {
  useAdminFeedback,
  useAdminFeedbackStats,
  type AdminFeedbackRow,
  type FeedbackFilters,
  type FeedbackType,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  FilterSelect,
  Pagination,
  formatTimestamp,
} from './shared';
import { FeedbackDrawer, FeedbackStatusBadge } from './FeedbackDrawer';

const PAGE_SIZE = 50;

const TYPE_LABEL: Record<FeedbackType, string> = {
  FEATURE_REQUEST: 'Feature',
  BUG_REPORT: 'Bug',
  FEEDBACK: 'Feedback',
  FRICTION: 'Friction',
};

export function FeedbackTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [type, setType] = useState<FeedbackFilters['type']>('any');
  const [status, setStatus] = useState<FeedbackFilters['status']>('any');
  const [offset, setOffset] = useState(0);

  const filters: FeedbackFilters = {
    type,
    status,
    limit: PAGE_SIZE,
    offset,
  };

  const list = useAdminFeedback(filters);
  const stats = useAdminFeedbackStats();
  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;
  const drawerId = searchParams.get('id');
  const drawerRow = drawerId ? (rows.find((r) => r.id === drawerId) ?? null) : null;

  const setDrawer = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('id', id);
    else next.delete('id');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <StatCard
          label="Total"
          value={stats.data ? stats.data.total : DASH}
          loading={stats.isLoading}
        />
        <StatCard
          label="Unresolved"
          value={stats.data ? stats.data.unresolved_count : DASH}
          loading={stats.isLoading}
        />
        <StatCard
          label="Features"
          value={stats.data ? (stats.data.by_type.FEATURE_REQUEST ?? 0) : DASH}
          loading={stats.isLoading}
        />
        <StatCard
          label="Bugs"
          value={stats.data ? (stats.data.by_type.BUG_REPORT ?? 0) : DASH}
          loading={stats.isLoading}
        />
        <StatCard
          label="Friction"
          value={stats.data ? (stats.data.by_type.FRICTION ?? 0) : DASH}
          loading={stats.isLoading}
        />
        <StatCard
          label="Avg rating"
          value={stats.data?.avg_rating ?? DASH}
          loading={stats.isLoading}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Type"
          value={type ?? 'any'}
          onChange={(v) => {
            setType(v as FeedbackFilters['type']);
            setOffset(0);
          }}
          options={[
            { value: 'any', label: 'Any' },
            { value: 'FEATURE_REQUEST', label: 'Feature' },
            { value: 'BUG_REPORT', label: 'Bug' },
            { value: 'FEEDBACK', label: 'Feedback' },
            { value: 'FRICTION', label: 'Friction' },
          ]}
        />
        <FilterSelect
          label="Status"
          value={status ?? 'any'}
          onChange={(v) => {
            setStatus(v as FeedbackFilters['status']);
            setOffset(0);
          }}
          options={[
            { value: 'any', label: 'Any' },
            { value: 'NEW', label: 'New' },
            { value: 'ACCEPTED', label: 'Accepted' },
            { value: 'IN_PROGRESS', label: 'In progress' },
            { value: 'FIXED', label: 'Fixed' },
            { value: 'IGNORED', label: 'Ignored' },
          ]}
        />
      </div>

      <DataTableShell
        loading={list.isLoading}
        error={list.error}
        empty={rows.length === 0}
        emptyLabel="No feedback yet"
      >
        <DataTable<AdminFeedbackRow>
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => setDrawer(r.id)}
          columns={[
            {
              key: 'type',
              label: 'Type',
              render: (r) => <Badge variant="outline">{TYPE_LABEL[r.type]}</Badge>,
            },
            {
              key: 'status',
              label: 'Status',
              render: (r) => <FeedbackStatusBadge status={r.status} />,
            },
            {
              key: 'title',
              label: 'Title',
              render: (r) => <span className="font-medium">{r.title}</span>,
            },
            {
              key: 'agent',
              label: 'Agent',
              // Anonymous-path rows pin to the fantopy-spectator system
              // agent; when the backend resolved the real reporter from
              // metadata, show them instead so triage knows who hit it.
              render: (r) =>
                r.reporter_name ? (
                  <span>
                    {r.reporter_name}
                    <span className="text-muted-foreground"> · reported</span>
                  </span>
                ) : (
                  (r.agent_name ?? DASH)
                ),
            },
            {
              key: 'rating',
              label: 'Rating',
              render: (r) =>
                r.rating == null ? (
                  <span className="text-muted-foreground">{DASH}</span>
                ) : (
                  <span>{'★'.repeat(r.rating)}</span>
                ),
            },
            {
              key: 'created_at',
              label: 'Created at',
              render: (r) => formatTimestamp(r.created_at),
            },
          ]}
        />
        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </DataTableShell>

      <FeedbackDrawer item={drawerRow} onClose={() => setDrawer(null)} />
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: React.ReactNode;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{loading ? '…' : value}</div>
      </CardContent>
    </Card>
  );
}
